/*
 * WOLV scroll-reveal — subtle, premium fade + slide-up.
 *
 * Art direction: motion should read as smooth continuity, never a trick.
 * A short fade combined with a small upward translate, one soft ease-out
 * curve, calm durations, and a light left-to-right stagger for grids so a
 * section reads as one choreography.
 *
 * All timing lives in CSS custom properties (see wolv-design.css) so the
 * feel is uniform and tunable in one place, and the *hidden* state is applied
 * by structural CSS at first paint. This file only decides what receives
 * .is-revealed (and when), and sets the per-item stagger index.
 *
 * Scope: only content sections inside <main id="MainContent">. That
 * naturally excludes the header, announcement bar, footer, cart drawer,
 * sticky nav and modals, which live outside <main> and must stay visible.
 */
(function () {
  'use strict';

  var main = document.getElementById('MainContent');
  if (!main) return;

  // Respect Reduce Motion and lack of IntersectionObserver: show everything
  // by undoing the pre-paint hidden state, then stop.
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduceMotion || !('IntersectionObserver' in window)) {
    document.documentElement.classList.remove('wolv-reveal-ready');
    return;
  }

  // Tell the inline failsafe that reveal is live, so it won't un-hide.
  window.__wolvRevealInit = true;

  // Containers whose children reveal as a staggered group.
  var GRID_SELECTOR =
    '.product-grid, .resource-list:not(.resource-list__carousel), .wolv-stats__grid';

  // Critical / interactive sections that must never animate — kept fully
  // visible by the matching CSS opt-out; JS leaves them alone.
  // product-recommendations hydrates its cards via AJAX after init, so the
  // observer would never see the replacement cards and they'd stay hidden —
  // exclude the whole section from reveal.
  var SKIP_SELECTOR =
    'product-information, cart-items, .sticky-add-to-cart, product-recommendations';

  // Heroes open the page above the fold, so a scroll reveal fires immediately
  // on load rather than on scroll — it holds back the LCP image and reads as a
  // page still loading. They stay visible via the matching CSS opt-out; here we
  // only make sure they are never observed.
  //   .hero-wrapper  — the `hero` section type (homepage)
  //   :first-child   — the section that opens a page (gym page hero)
  //   .sup-hero      — SUP hero, which sits behind a schema-only section
  function isHero(section) {
    return section.matches('.hero-wrapper, :first-child') || !!section.querySelector('.sup-hero');
  }

  var observer = new IntersectionObserver(
    function (entries) {
      for (var i = 0; i < entries.length; i++) {
        var entry = entries[i];
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('is-revealed');
        observer.unobserve(entry.target);
      }
    },
    {
      root: null,
      // Fire just before the element is comfortably in view — a reveal that
      // triggers once the element is already fully visible reads cheap.
      rootMargin: '0px 0px -12% 0px',
      threshold: 0
    }
  );

  function elementChildren(node) {
    var out = [];
    var kids = node.children;
    for (var i = 0; i < kids.length; i++) {
      if (kids[i].nodeType === 1) out.push(kids[i]);
    }
    return out;
  }

  function markSection(section) {
    if (!section || section.hasAttribute('data-wolv-reveal-done')) return;
    section.setAttribute('data-wolv-reveal-done', '');

    // Heroes and critical/interactive sections stay still (CSS keeps them visible).
    if (isHero(section)) return;
    if (section.matches(SKIP_SELECTOR) || section.querySelector(SKIP_SELECTOR)) return;

    var grid = section.querySelector(GRID_SELECTOR);
    var items = grid ? elementChildren(grid) : [];

    if (grid && items.length > 1) {
      // Reveal each item on its own as it scrolls in; the wrapper stays put
      // (CSS opt-out) so the heading holds its place and nothing double-moves.
      // No artificial per-item delay — side-by-side cards reveal together,
      // stacked cards cascade naturally with the scroll.
      for (var i = 0; i < items.length; i++) {
        observer.observe(items[i]);
      }
    } else {
      observer.observe(section);
    }
  }

  function init() {
    var kids = elementChildren(main);
    for (var i = 0; i < kids.length; i++) {
      if (kids[i].classList.contains('shopify-section')) markSection(kids[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor: re-scan a section when it is reloaded so the preview keeps
  // animating without a full refresh.
  document.addEventListener('shopify:section:load', function (event) {
    var section =
      event.target && event.target.closest ? event.target.closest('.shopify-section') : null;
    if (section && main.contains(section)) {
      section.removeAttribute('data-wolv-reveal-done');
      markSection(section);
    }
  });
})();
